import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { cn, formatCurrency } from "@/lib/utils";
import { toast } from "sonner";
import EmptyState from "@/components/common/EmptyState";
import {
  Search, ChevronLeft, ChevronRight, ExternalLink,
  Loader2, FileText, Plus, X, Trash2, Save,
  Mail, Phone, MapPin, Building2, ChevronDown,
  CheckCircle2, Clock3, XCircle, TrendingUp,
  CreditCard, Copy, Send,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const CACHE_KEY = "parasut_sales_offers_cache_v2";
const CACHE_TTL_MS = 5 * 60 * 1000;
const CURRENCIES = ["TRL", "USD", "EUR", "GBP"];
const VAT_RATES = [0, 1, 8, 10, 18, 20];

// ─── Helpers ──────────────────────────────────────────────────────────────

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const months = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];
  return `${String(d.getDate()).padStart(2,"0")} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function statusMeta(status) {
  switch ((status || "").toLowerCase()) {
    case "waiting":  return { label: "Cevap Bekleniyor", cls: "bg-amber-50 text-amber-700 border-amber-200",   dot: "bg-amber-500",   icon: Clock3 };
    case "accepted": return { label: "Kabul Edildi",     cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500", icon: CheckCircle2 };
    case "rejected": return { label: "Reddedildi",       cls: "bg-rose-50 text-rose-700 border-rose-200",      dot: "bg-rose-500",    icon: XCircle };
    default:         return { label: status || "Belirsiz", cls: "bg-gray-50 text-gray-600 border-gray-200",    dot: "bg-gray-400",    icon: Clock3 };
  }
}

function StatusBadge({ status }) {
  const m = statusMeta(status);
  const Icon = m.icon;
  return (
    <span className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border", m.cls)}>
      <Icon className="h-3 w-3" />
      {m.label}
    </span>
  );
}

function readCache()      { try { const r = sessionStorage.getItem(CACHE_KEY); if (!r) return null; const p = JSON.parse(r); return Date.now()-p.ts>CACHE_TTL_MS?null:p.data; } catch { return null; } }
function writeCache(data) { try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ts:Date.now(),data})); } catch {} }
function clearCache()     { try { sessionStorage.removeItem(CACHE_KEY); } catch {} }

// ─── Contact info strip ────────────────────────────────────────────────────

function ContactInfoStrip({ contact }) {
  if (!contact) return null;
  const lines = [
    contact.phone   && { icon: Phone,     text: contact.phone },
    contact.email   && { icon: Mail,      text: contact.email },
    (contact.district || contact.city) && { icon: MapPin, text: [contact.district, contact.city].filter(Boolean).join(", ") },
    (contact.tax_office || contact.tax_number) && { icon: Building2, text: [contact.tax_office, contact.tax_number].filter(Boolean).join(" · ") },
  ].filter(Boolean);
  if (!lines.length) return null;
  return (
    <div className="mt-2 rounded-lg bg-muted/50 border border-border px-3 py-2.5 flex flex-wrap gap-x-5 gap-y-1.5">
      {lines.map(({ icon: Icon, text }, i) => (
        <span key={i} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <Icon className="h-3 w-3 shrink-0" />{text}
        </span>
      ))}
    </div>
  );
}

// ─── Contact select ────────────────────────────────────────────────────────

function ContactSelect({ contactId, contactName, onSelect }) {
  const [query, setQuery]       = useState(contactName || "");
  const [open, setOpen]         = useState(false);
  const [results, setResults]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [contactInfo, setContactInfo] = useState(null);
  const timer   = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!contactId) return;
    api.get(`/parasut/contacts?search=${encodeURIComponent(contactName || "")}&page=1&per_page=5`)
      .then((res) => {
        const match = (res.contacts || []).find((c) => c.id === contactId);
        if (match) setContactInfo(match);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function h(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await api.get(`/parasut/contacts?search=${encodeURIComponent(q)}&page=1&per_page=25`);
      setResults(res.contacts || []);
    } catch { setResults([]); }
    setLoading(false);
  }, []);

  function handleInput(val) {
    setQuery(val);
    setContactInfo(null);
    onSelect(null, null);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => doSearch(val), 350);
    setOpen(true);
  }

  function handlePick(c) {
    setQuery(c.name);
    setContactInfo(c);
    onSelect(c.id, c.name);
    setOpen(false);
    setResults([]);
  }

  return (
    <div ref={wrapRef}>
      <Input
        value={query}
        placeholder="Müşteri ara... (en az 2 karakter)"
        onFocus={() => { if (query.length >= 2 && !contactInfo) { setOpen(true); doSearch(query); } }}
        onChange={(e) => handleInput(e.target.value)}
      />
      {open && !contactInfo && (
        <div className="absolute z-30 mt-1 w-full bg-background border border-border rounded-lg shadow-lg max-h-52 overflow-y-auto">
          {loading
            ? <div className="p-3 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin"/>Aranıyor...</div>
            : results.length === 0
              ? <div className="p-3 text-sm text-muted-foreground">{query.length < 2 ? "En az 2 karakter girin" : "Sonuç bulunamadı"}</div>
              : results.map((c) => (
                <button key={c.id} type="button"
                  className="w-full text-left px-3 py-2.5 text-sm hover:bg-muted border-b last:border-0 border-border"
                  onMouseDown={(e) => { e.preventDefault(); handlePick(c); }}>
                  <div className="font-medium text-foreground">{c.name}</div>
                  {(c.email || c.city) && (
                    <div className="text-xs text-muted-foreground mt-0.5 flex gap-3">
                      {c.email && <span>{c.email}</span>}
                      {c.city  && <span>{c.city}</span>}
                    </div>
                  )}
                </button>
              ))
          }
        </div>
      )}
      <ContactInfoStrip contact={contactInfo} />
    </div>
  );
}

// ─── Product row ───────────────────────────────────────────────────────────

function ProductRowInput({ item, idx, allProducts, loadingProducts, onChange, onRemove, canRemove }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState(item.description || "");
  const wrapRef = useRef(null);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? allProducts.filter((p) => (p.name || "").toLowerCase().includes(q)).slice(0, 30)
    : allProducts.slice(0, 30);

  useEffect(() => {
    function h(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  function handlePick(p) {
    setQuery(p.name);
    onChange(idx, "description", p.name);
    onChange(idx, "product_id", p.id || "");
    const price = p.list_price || p.sales_price;
    if (price) onChange(idx, "unit_price", price);
    if (p.vat_rate != null) onChange(idx, "vat_rate", parseInt(p.vat_rate) || 20);
    setOpen(false);
  }

  return (
    <div className="p-3 border border-border rounded-lg space-y-2 relative bg-card">
      {canRemove && (
        <button type="button" onClick={() => onRemove(idx)} className="absolute top-2 right-2 text-muted-foreground hover:text-rose-500 transition-colors">
          <Trash2 className="h-3.5 w-3.5"/>
        </button>
      )}
      <div ref={wrapRef} className="relative">
        <Input
          value={query}
          placeholder="Ürün / hizmet adı..."
          onChange={(e) => {
            setQuery(e.target.value);
            onChange(idx, "description", e.target.value);
            onChange(idx, "product_id", "");
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
        {open && (
          <div className="absolute z-30 mt-1 w-full bg-background border border-border rounded-lg shadow-lg max-h-44 overflow-y-auto">
            {loadingProducts
              ? <div className="p-2 text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin"/>Ürünler yükleniyor...</div>
              : filtered.length === 0
                ? <div className="p-2 text-xs text-muted-foreground">Ürün bulunamadı — serbest metin girebilirsiniz</div>
                : filtered.map((p) => (
                  <button key={p.id} type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted border-b last:border-0 border-border"
                    onMouseDown={(e) => { e.preventDefault(); handlePick(p); }}>
                    <span className="text-foreground">{p.name}</span>
                    {p.list_price && <span className="text-muted-foreground text-xs ml-2">{p.list_price} {p.currency || ""}</span>}
                  </button>
                ))
            }
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-xs text-muted-foreground">Miktar</label>
          <Input type="number" min="0.01" step="0.01" value={item.quantity}
            onChange={(e) => onChange(idx, "quantity", e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Birim Fiyat *</label>
          <Input type="number" min="0" step="0.01" value={item.unit_price}
            onChange={(e) => onChange(idx, "unit_price", e.target.value)} placeholder="0.00" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">KDV %</label>
          <select value={item.vat_rate} onChange={(e) => onChange(idx, "vat_rate", parseInt(e.target.value))}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
            {VAT_RATES.map((r) => <option key={r} value={r}>%{r}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-muted-foreground">İndirim Tipi</label>
          <select value={item.discount_type || "percentage"} onChange={(e) => onChange(idx, "discount_type", e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
            <option value="percentage">Yüzde (%)</option>
            <option value="amount">Tutar</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">İndirim Değeri</label>
          <Input type="number" min="0" step="0.01" value={item.discount_value ?? 0}
            onChange={(e) => onChange(idx, "discount_value", e.target.value)} placeholder="0" />
        </div>
      </div>
    </div>
  );
}

// ─── Currency picker ───────────────────────────────────────────────────────

function CurrencyPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-border bg-background text-foreground hover:bg-muted transition-colors">
        {value}<ChevronDown className="h-3 w-3 opacity-60"/>
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 bg-background border border-border rounded-lg shadow-lg overflow-hidden">
          {CURRENCIES.map((c) => (
            <button key={c} type="button"
              className={cn("block w-full text-left px-4 py-2 text-sm hover:bg-muted",
                c === value ? "font-semibold text-blue-600" : "text-foreground")}
              onMouseDown={(e) => { e.preventDefault(); onChange(c); setOpen(false); }}>
              {c}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Status Selector ───────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: "waiting",  label: "Cevap Bekleniyor", icon: Clock3,       cls: "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100",  activeCls: "border-amber-400 bg-amber-100 text-amber-800 ring-2 ring-amber-300" },
  { value: "accepted", label: "Kabul Edildi",      icon: CheckCircle2, cls: "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100", activeCls: "border-emerald-400 bg-emerald-100 text-emerald-800 ring-2 ring-emerald-300" },
  { value: "rejected", label: "Reddedildi",        icon: XCircle,      cls: "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100",    activeCls: "border-rose-400 bg-rose-100 text-rose-800 ring-2 ring-rose-300" },
];

function StatusSelector({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {STATUS_OPTIONS.map((opt) => {
        const Icon = opt.icon;
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all",
              active ? opt.activeCls : opt.cls
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Offer Form ────────────────────────────────────────────────────────────

function OfferForm({ initial, onSubmit, submitting, onClose, submitLabel, showStatus = false }) {
  const [form, setForm]               = useState(initial);
  const [allProducts, setAllProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  useEffect(() => {
    setLoadingProducts(true);
    api.get("/parasut/products?search=&page=1&per_page=25")
      .then((res) => { setAllProducts(res.items || []); setLoadingProducts(false); })
      .catch(() => setLoadingProducts(false));
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setItem = useCallback((idx, k, v) => setForm((f) => {
    const items = [...f.items];
    items[idx] = { ...items[idx], [k]: v };
    return { ...f, items };
  }), []);

  const addItem = () => setForm((f) => ({
    ...f,
    items: [...f.items, { description: "", quantity: 1, unit_price: "", vat_rate: 20, product_id: "", discount_type: "percentage", discount_value: 0 }],
  }));

  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.contact_id) { toast.error("Müşteri seçiniz"); return; }
    for (const item of form.items) {
      if (item.unit_price === "" || isNaN(parseFloat(item.unit_price))) {
        toast.error("Tüm satırlara birim fiyat giriniz"); return;
      }
    }
    onSubmit(form);
  }

  return (
    <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">

      {/* Status selector — only in edit mode */}
      {showStatus && (
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-2">Teklif Durumu</label>
          <StatusSelector value={form.status} onChange={(v) => setField("status", v)} />
        </div>
      )}

      {/* Dates + currency */}
      <div className="flex items-start gap-4">
        <div className="flex-1 grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Teklif Tarihi</label>
            <Input type="date" value={form.issue_date || ""} onChange={(e) => setField("issue_date", e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Geçerlilik Tarihi</label>
            <Input type="date" value={form.due_date || ""} onChange={(e) => setField("due_date", e.target.value)} />
          </div>
        </div>
        <div className="pt-5 shrink-0">
          <CurrencyPicker value={form.currency} onChange={(c) => setField("currency", c)} />
        </div>
      </div>

      {/* Contact */}
      <div className="relative">
        <label className="block text-xs font-medium text-muted-foreground mb-1">Müşteri *</label>
        <ContactSelect
          contactId={form.contact_id}
          contactName={form.contact_name}
          onSelect={(id, name) => { setField("contact_id", id || ""); setField("contact_name", name || ""); }}
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">Açıklama</label>
        <Input value={form.description} onChange={(e) => setField("description", e.target.value)} placeholder="Teklif açıklaması" />
      </div>

      {/* Line Items */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-muted-foreground">Ürün / Hizmet Satırları</label>
          <button type="button" onClick={addItem} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 transition-colors">
            <Plus className="h-3.5 w-3.5"/>Satır Ekle
          </button>
        </div>
        <div className="space-y-3">
          {form.items.map((item, idx) => (
            <ProductRowInput
              key={idx}
              item={item}
              idx={idx}
              allProducts={allProducts}
              loadingProducts={loadingProducts}
              onChange={setItem}
              onRemove={removeItem}
              canRemove={form.items.length > 1}
            />
          ))}
        </div>
      </div>

      {/* Note */}
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">Not</label>
        <textarea value={form.note || ""} onChange={(e) => setField("note", e.target.value)}
          placeholder="Teklife eklenecek not..." rows={2}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none" />
      </div>

      <div className="flex gap-3 justify-end pt-2 border-t border-border">
        <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>İptal</Button>
        <Button type="submit" disabled={submitting} className="gap-2">
          {submitting ? <><Loader2 className="h-4 w-4 animate-spin"/>Kaydediliyor...</> : <><Save className="h-4 w-4"/>{submitLabel}</>}
        </Button>
      </div>
    </form>
  );
}

// ─── Payment Panel ────────────────────────────────────────────────────────

function PaymentRequestModal({ offerId, contactEmail, onClose, onCreated }) {
  const [integrations, setIntegrations] = useState([]);
  const [selectedIntegration, setSelectedIntegration] = useState("");
  const [sendEmail, setSendEmail] = useState(Boolean(contactEmail));
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/integrations?provider=woocommerce")
      .then((res) => {
        const list = (Array.isArray(res) ? res : res?.items || []).filter(
          (i) => i.provider === "woocommerce" && i.status !== "deleted"
        );
        setIntegrations(list);
        if (list.length === 1) setSelectedIntegration(list[0].id);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  async function handleCreate() {
    if (!selectedIntegration) { toast.error("Bir Woo entegrasyonu seçin"); return; }
    setSubmitting(true);
    try {
      const res = await api.post(`/parasut/sales-offers/${offerId}/payment`, {
        integration_id: selectedIntegration,
        send_email: sendEmail && Boolean(contactEmail),
      });
      toast.success(res.email_sent ? "Ödeme linki oluşturuldu ve e-posta gönderildi" : "Ödeme linki oluşturuldu");
      onCreated(res);
    } catch (err) {
      toast.error(err?.message || "Ödeme linki oluşturulamadı");
    }
    setSubmitting(false);
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="bg-background rounded-2xl shadow-2xl w-full max-w-sm border border-border">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <CreditCard className="h-4 w-4 text-blue-600"/>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">Ödeme Linki Oluştur</h3>
              <p className="text-xs text-muted-foreground">WooCommerce üzerinden güvenli ödeme</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="h-4 w-4"/>
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-5 space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4 justify-center">
              <Loader2 className="h-4 w-4 animate-spin"/>Entegrasyonlar yükleniyor...
            </div>
          ) : integrations.length === 0 ? (
            <div className="text-sm text-rose-600 bg-rose-50 rounded-lg px-4 py-3 border border-rose-200">
              Aktif WooCommerce entegrasyonu bulunamadı.
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Mağaza</label>
                <select value={selectedIntegration} onChange={(e) => setSelectedIntegration(e.target.value)}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30">
                  <option value="">— Seçin —</option>
                  {integrations.map((i) => (
                    <option key={i.id} value={i.id}>{i.name || i.credentials?.store_url || i.id}</option>
                  ))}
                </select>
              </div>

              {contactEmail && (
                <label className={cn(
                  "flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors",
                  sendEmail ? "border-blue-200 bg-blue-50" : "border-border bg-background hover:bg-muted/30"
                )}>
                  <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-input accent-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-foreground">E-posta ile gönder</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Teklif detayları ve ödeme butonu ile birlikte <span className="font-medium">{contactEmail}</span> adresine gönderilir.
                    </p>
                  </div>
                </label>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 justify-end px-5 py-4 border-t border-border">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>İptal</Button>
          <Button size="sm" onClick={handleCreate}
            disabled={submitting || !selectedIntegration || integrations.length === 0}
            className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-white">
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <CreditCard className="h-3.5 w-3.5"/>}
            Link Oluştur
          </Button>
        </div>
      </div>
    </div>
  );
}

function PaymentBanner({ payment, onRequestNew }) {
  const isPending = payment.status === "pending_payment";
  const isPaid    = payment.status === "paid";

  function copyLink() {
    navigator.clipboard.writeText(payment.payment_url).then(() => toast.success("Link kopyalandı"));
  }

  return (
    <div className={cn(
      "mx-6 mt-4 rounded-xl border overflow-hidden",
      isPaid    ? "border-emerald-200 bg-emerald-50"  :
      isPending ? "border-blue-200 bg-blue-50"        :
                  "border-gray-200 bg-gray-50"
    )}>
      {/* Status row */}
      <div className={cn(
        "flex items-center justify-between px-4 py-2.5",
        isPaid    ? "bg-emerald-100/60" :
        isPending ? "bg-blue-100/60"    :
                    "bg-gray-100/60"
      )}>
        <div className="flex items-center gap-2">
          <span className={cn("h-2 w-2 rounded-full",
            isPaid ? "bg-emerald-500" : isPending ? "bg-blue-500 animate-pulse" : "bg-gray-400"
          )}/>
          <span className={cn("text-xs font-semibold",
            isPaid ? "text-emerald-700" : isPending ? "text-blue-700" : "text-gray-500"
          )}>
            {isPaid ? "Ödeme Alındı" : isPending ? "Ödeme Bekleniyor" : "İptal Edildi"}
          </span>
        </div>
        {!isPaid && (
          <button onClick={onRequestNew}
            className="text-xs text-muted-foreground hover:text-foreground underline transition-colors">
            Yeniden oluştur
          </button>
        )}
      </div>

      {/* Link row */}
      {payment.payment_url && !isPaid && (
        <div className="px-4 py-3 space-y-2.5">
          <div className="flex items-center gap-2 bg-white/70 rounded-lg px-3 py-2 border border-white/80">
            <span className="text-xs text-muted-foreground truncate flex-1 font-mono">{payment.payment_url}</span>
            <button onClick={copyLink} title="Kopyala"
              className="shrink-0 text-muted-foreground hover:text-foreground transition-colors">
              <Copy className="h-3.5 w-3.5"/>
            </button>
            <a href={payment.payment_url} target="_blank" rel="noopener noreferrer" title="Aç"
              className="shrink-0 text-muted-foreground hover:text-foreground transition-colors">
              <ExternalLink className="h-3.5 w-3.5"/>
            </a>
          </div>
          {payment.contact_email && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Mail className="h-3 w-3 shrink-0"/>
              <span>Gönderildi: <span className="font-medium">{payment.contact_email}</span></span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Offer totals breakdown ────────────────────────────────────────────────

function OfferTotals({ offer }) {
  const cur = offer.currency === "TRL" ? "TRY" : (offer.currency || "TRY");
  const fmt = (v) => v != null ? formatCurrency(parseFloat(v) || 0, cur) : null;
  const subtotal     = parseFloat(offer.subtotal)      || 0;
  const discount     = parseFloat(offer.total_discount) || 0;
  const brut         = subtotal - discount;
  const vat          = parseFloat(offer.total_vat)     || 0;
  const genelToplam  = parseFloat(offer.net_total)     || 0;
  return (
    <div className="px-6 pt-3 pb-3 text-xs text-right space-y-1">
      {subtotal > 0 && (
        <div className="flex justify-between text-muted-foreground">
          <span>Ara Toplam</span><span className="tabular-nums">{fmt(subtotal)}</span>
        </div>
      )}
      {discount > 0 && (
        <div className="flex justify-between text-rose-600">
          <span>Satır İndirimi</span><span className="tabular-nums">- {fmt(discount)}</span>
        </div>
      )}
      {discount > 0 && (
        <div className="flex justify-between text-muted-foreground">
          <span>Brüt Toplam</span><span className="tabular-nums">{fmt(brut)}</span>
        </div>
      )}
      {vat > 0 && (
        <div className="flex justify-between text-muted-foreground">
          <span>Toplam KDV</span><span className="tabular-nums">{fmt(vat)}</span>
        </div>
      )}
      <div className="flex justify-between font-semibold text-foreground text-sm border-t border-border pt-1 mt-1">
        <span>Genel Toplam</span><span className="tabular-nums">{fmt(genelToplam)}</span>
      </div>
    </div>
  );
}

// ─── Modal shell ───────────────────────────────────────────────────────────

function ModalShell({ title, onClose, children, headerExtra }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="bg-background rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-border">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-[linear-gradient(120deg,#f7fbff_0%,#f8fafc_55%,#f1f5f9_100%)] dark:bg-none dark:bg-card rounded-t-xl">
          <h2 className="text-base font-semibold text-foreground truncate pr-4">{title}</h2>
          <div className="flex items-center gap-3 shrink-0">
            {headerExtra}
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
              <X className="h-5 w-5"/>
            </button>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── Create Modal ──────────────────────────────────────────────────────────

function CreateModal({ onClose, onCreated }) {
  const [submitting, setSubmitting] = useState(false);
  const initial = {
    contact_id: "", contact_name: "", description: "",
    issue_date: new Date().toISOString().split("T")[0],
    due_date: "", currency: "TRL", note: "",
    items: [{ description: "", quantity: 1, unit_price: "", vat_rate: 20, product_id: "", discount_type: "percentage", discount_value: 0 }],
  };

  async function handleSubmit(form) {
    setSubmitting(true);
    try {
      await api.post("/parasut/sales-offers", {
        contact_id: form.contact_id,
        description: form.description,
        issue_date: form.issue_date || undefined,
        due_date: form.due_date || undefined,
        currency: form.currency,
        note: form.note || undefined,
        items: form.items.map((it) => ({
          description: it.description,
          quantity: parseFloat(it.quantity) || 1,
          unit_price: parseFloat(it.unit_price) || 0,
          vat_rate: parseInt(it.vat_rate) || 0,
          discount_type: it.discount_type || "percentage",
          discount_value: parseFloat(it.discount_value) || 0,
          product_id: it.product_id || undefined,
        })),
      });
      toast.success("Teklif oluşturuldu");
      onCreated();
    } catch (err) {
      toast.error(err?.message || "Teklif oluşturulamadı");
    }
    setSubmitting(false);
  }

  return (
    <ModalShell title="Yeni Teklif Oluştur" onClose={onClose}>
      <OfferForm initial={initial} onSubmit={handleSubmit} submitting={submitting} onClose={onClose} submitLabel="Teklif Oluştur" />
    </ModalShell>
  );
}

// ─── Edit Modal ────────────────────────────────────────────────────────────

function EditModal({ offerId, onClose, onSaved }) {
  const [offer, setOffer]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState(null);
  const [payment, setPayment]       = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);

  useEffect(() => {
    api.get(`/parasut/sales-offers/${offerId}`)
      .then((res) => { setOffer(res); setLoading(false); })
      .catch((e) => { setError(e?.message || "Teklif yüklenemedi"); setLoading(false); });
    // Load payment status
    api.get(`/parasut/sales-offers/${offerId}/payment`)
      .then((res) => { if (res.exists) setPayment(res); })
      .catch(() => {});
  }, [offerId]);

  async function handleSubmit(form) {
    setSubmitting(true);
    try {
      await api.patch(`/parasut/sales-offers/${offerId}`, {
        contact_id: form.contact_id || undefined,
        description: form.description,
        issue_date: form.issue_date || undefined,
        due_date: form.due_date || undefined,
        currency: form.currency,
        note: form.note || undefined,
        status: form.status || undefined,
        items: form.items.map((it) => ({
          description: it.description,
          quantity: parseFloat(it.quantity) || 1,
          unit_price: parseFloat(it.unit_price) || 0,
          vat_rate: parseInt(it.vat_rate) || 0,
          discount_type: it.discount_type || "percentage",
          discount_value: parseFloat(it.discount_value) || 0,
          product_id: it.product_id || undefined,
        })),
      });
      toast.success("Teklif güncellendi");
      onSaved();
    } catch (err) {
      toast.error(err?.message || "Teklif güncellenemedi");
    }
    setSubmitting(false);
  }

  if (loading) return (
    <ModalShell title="Teklif Düzenle" onClose={onClose}>
      <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground"/></div>
    </ModalShell>
  );
  if (error) return (
    <ModalShell title="Teklif Düzenle" onClose={onClose}>
      <div className="px-6 py-8 text-sm text-rose-600">{error}</div>
    </ModalShell>
  );

  const initial = {
    contact_id:   offer.contact_id   || "",
    contact_name: offer.contact_name || "",
    description:  offer.description  || "",
    issue_date:   offer.issue_date   || "",
    due_date:     offer.due_date     || "",
    currency:     offer.currency     || "TRL",
    note:         offer.note         || "",
    status:       offer.status       || "waiting",
    items: (offer.line_items || []).length > 0
      ? offer.line_items.map((li) => ({
          description:    li.product_name || li.description || "",
          quantity:       li.quantity  ?? 1,
          unit_price:     li.unit_price ?? "",
          vat_rate:       li.vat_rate  ?? 20,
          product_id:     li.product_id || "",
          discount_type:  li.discount_type  || "percentage",
          discount_value: li.discount_value ?? 0,
        }))
      : [{ description: "", quantity: 1, unit_price: "", vat_rate: 20, product_id: "" }],
  };

  return (
    <>
      {showPayModal && (
        <PaymentRequestModal
          offerId={offerId}
          contactEmail={offer?.contact_email || null}
          onClose={() => setShowPayModal(false)}
          onCreated={(res) => { setPayment({ exists: true, ...res }); setShowPayModal(false); }}
        />
      )}
    <ModalShell
      title={offer.description || `Teklif #${offerId}`}
      onClose={onClose}
      headerExtra={
        <div className="flex items-center gap-2">
          {!payment && (
            <button
              type="button"
              onClick={() => setShowPayModal(true)}
              className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 border border-blue-200 bg-blue-50 hover:bg-blue-100 px-2.5 py-1 rounded-md transition-colors"
            >
              <CreditCard className="h-3.5 w-3.5"/>Ödeme Al
            </button>
          )}
          {offer.parasut_url && (
            <a href={offer.parasut_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <ExternalLink className="h-3.5 w-3.5"/>Paraşüt
            </a>
          )}
        </div>
      }
    >
      {payment && (
        <PaymentBanner
          payment={payment}
          onRequestNew={() => setShowPayModal(true)}
        />
      )}
      {(offer.subtotal != null || offer.net_total != null) && (
        <OfferTotals offer={offer} />
      )}
      <div className="border-t border-border"/>
      <OfferForm
        initial={initial}
        onSubmit={handleSubmit}
        submitting={submitting}
        onClose={onClose}
        submitLabel="Kaydet"
        showStatus={true}
      />
    </ModalShell>
    </>
  );
}

// ─── Summary cards ─────────────────────────────────────────────────────────

function SummaryCards({ offers }) {
  const waiting  = offers.filter((o) => o.status === "waiting").length;
  const accepted = offers.filter((o) => o.status === "accepted").length;
  const rejected = offers.filter((o) => o.status === "rejected").length;
  const total = offers.reduce((s, o) => s + (parseFloat(o.gross_total) || 0), 0);

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <div className="rounded-xl border border-amber-200/70 bg-gradient-to-br from-amber-50 to-white dark:from-amber-950/20 dark:to-card p-3 shadow-[0_4px_12px_rgba(245,158,11,0.08)]">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-amber-700 dark:text-amber-400">Bekleyenler</span>
          <Clock3 className="h-4 w-4 text-amber-500" />
        </div>
        <div className="mt-2 text-xl font-semibold tabular-nums text-amber-900 dark:text-amber-200">{waiting}</div>
      </div>
      <div className="rounded-xl border border-emerald-200/70 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/20 dark:to-card p-3 shadow-[0_4px_12px_rgba(16,185,129,0.08)]">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">Kabul Edilenler</span>
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
        </div>
        <div className="mt-2 text-xl font-semibold tabular-nums text-emerald-900 dark:text-emerald-200">{accepted}</div>
      </div>
      <div className="rounded-xl border border-rose-200/70 bg-gradient-to-br from-rose-50 to-white dark:from-rose-950/20 dark:to-card p-3 shadow-[0_4px_12px_rgba(244,63,94,0.08)]">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-rose-700 dark:text-rose-400">Reddedilenler</span>
          <XCircle className="h-4 w-4 text-rose-500" />
        </div>
        <div className="mt-2 text-xl font-semibold tabular-nums text-rose-900 dark:text-rose-200">{rejected}</div>
      </div>
      <div className="rounded-xl border border-sky-200/70 bg-gradient-to-br from-sky-50 to-white dark:from-sky-950/20 dark:to-card p-3 shadow-[0_4px_12px_rgba(14,165,233,0.08)]">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-sky-700 dark:text-sky-400">Toplam Tutar</span>
          <TrendingUp className="h-4 w-4 text-sky-500" />
        </div>
        <div className="mt-2 text-sm font-semibold tabular-nums text-sky-900 dark:text-sky-200">{formatCurrency(total)}</div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────

export default function TekliflerPage() {
  const [offers, setOffers]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [page, setPage]                 = useState(1);
  const [total, setTotal]               = useState(0);
  const [search, setSearch]             = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreate, setShowCreate]     = useState(false);
  const [editingId, setEditingId]       = useState(null);
  const perPage = 20;

  const fetchOffers = useCallback(async (pg = 1) => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ page: pg, per_page: perPage });
      if (statusFilter !== "all") params.set("status", statusFilter);
      const res = await api.get(`/parasut/sales-offers?${params}`);
      setOffers(res.offers || []);
      setTotal(res.total || 0);
    } catch (err) { setError(err?.message || "Teklifler yüklenemedi"); }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { setPage(1); fetchOffers(1); }, [fetchOffers]); // always fresh — no cache

  const filtered = offers.filter((o) => {
    const q = search.trim().toLowerCase();
    return !q || [o.description, o.contact_name].some((v) => String(v || "").toLowerCase().includes(q));
  });

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="page-shell space-y-6 min-h-[calc(100dvh-3.5rem)] overflow-x-hidden">
      {showCreate && (
        <CreateModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchOffers(1); }} />
      )}
      {editingId && (
        <EditModal offerId={editingId} onClose={() => setEditingId(null)} onSaved={() => { setEditingId(null); fetchOffers(page); }} />
      )}

      <div className="page-header">
        <div className="app-page-header">
          <h1 className="app-page-title">Teklifler</h1>
          <p className="app-page-subtitle">{!loading && total > 0 ? `${total} teklif` : "Müşterilerinize teklif oluşturun ve takip edin."}</p>
        </div>
        <div className="page-header-actions">
          <div className="flex items-center gap-3 rounded-lg border bg-background px-3 h-10 w-64">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Teklif veya müşteri ara..."
              className="flex-1 bg-transparent text-sm outline-none"
            />
          </div>
          <Button onClick={() => setShowCreate(true)} className="gap-2 h-10 shrink-0">
            <Plus className="h-4 w-4"/>
            Yeni Teklif
          </Button>
        </div>
      </div>

      <div className="surface-card overflow-hidden">
        <div className="sheet-heading">
          <div>
            <h2>Teklifler</h2>
            {!loading && total > 0 && <p>{total} teklif</p>}
          </div>
        </div>

        {/* Summary cards */}
        {!loading && offers.length > 0 && (
          <div className="border-b border-border bg-card/60 p-3">
            <SummaryCards offers={offers} />
          </div>
        )}

        {/* Status filter tabs */}
        <div className="border-b border-border bg-card/40 px-4 py-2 flex items-center gap-1.5 overflow-x-auto">
          {[
            { key: "all",      label: "Tümü" },
            { key: "waiting",  label: "Bekleyenler" },
            { key: "accepted", label: "Kabul Edilenler" },
            { key: "rejected", label: "Reddedilenler" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded-md border whitespace-nowrap transition-colors",
                statusFilter === f.key
                  ? "bg-foreground text-background border-foreground"
                  : "border-border text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-card/70">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Teklifler yükleniyor...</span>
            </div>
          ) : error ? (
            <div className="m-4 rounded-lg border border-rose-200 bg-rose-50 dark:bg-rose-900/20 p-4 text-sm text-rose-700 dark:text-rose-400">{error}</div>
          ) : filtered.length === 0 ? (
            offers.length === 0 ? (
              <EmptyState
                icon={<FileText strokeWidth={1.5} />}
                title="Henüz teklif yok"
                desc="Paraşüt satış teklifleriniz burada görünür."
                action={{ label: "Yeni Teklif Oluştur", onClick: () => setShowCreate(true) }}
              />
            ) : (
              <EmptyState
                icon={<Search strokeWidth={1.5} />}
                title="Teklif bulunamadı"
                desc="Arama veya filtre kriterlerinizi değiştirin."
                size="sm"
              />
            )
          ) : (
            <>
              {/* Mobile cards */}
              <div className="md:hidden space-y-2 p-3">
                {filtered.map((offer) => (
                  <button
                    key={offer.id}
                    type="button"
                    onClick={() => setEditingId(offer.id)}
                    className="w-full rounded-xl border border-border bg-card p-3 text-left hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-foreground truncate">{offer.description || `Teklif #${offer.id}`}</div>
                        <div className="text-xs text-muted-foreground mt-0.5 truncate">{offer.contact_name || "-"}</div>
                      </div>
                      <StatusBadge status={offer.status} />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                      <span>{formatDate(offer.issue_date)}</span>
                      <span className="font-semibold text-foreground text-sm">
                        {offer.gross_total != null ? formatCurrency(parseFloat(offer.gross_total), offer.currency === "TRL" ? "TRY" : offer.currency) : "-"}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden md:block">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/40">
                      <th className="text-left px-5 py-3 text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Açıklama</th>
                      <th className="text-left px-4 py-3 text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Müşteri</th>
                      <th className="text-left px-4 py-3 text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Tarih</th>
                      <th className="text-right px-4 py-3 text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Tutar</th>
                      <th className="text-left px-4 py-3 text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Durum</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filtered.map((offer) => (
                      <tr
                        key={offer.id}
                        onClick={() => setEditingId(offer.id)}
                        className="hover:bg-muted/30 transition-colors cursor-pointer group"
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-foreground group-hover:text-blue-600 transition-colors">
                            {offer.description || `Teklif #${offer.id}`}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{offer.contact_name || "-"}</td>
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                          {formatDate(offer.issue_date)}
                          {offer.due_date && <div className="text-xs text-muted-foreground/60">Son: {formatDate(offer.due_date)}</div>}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-foreground whitespace-nowrap tabular-nums">
                          {offer.gross_total != null ? formatCurrency(parseFloat(offer.gross_total), offer.currency === "TRL" ? "TRY" : offer.currency) : "-"}
                        </td>
                        <td className="px-4 py-3"><StatusBadge status={offer.status}/></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border text-sm text-muted-foreground">
                  <span>{total} teklif</span>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={page===1} onClick={()=>{setPage(page-1);fetchOffers(page-1);}}>
                      <ChevronLeft className="h-4 w-4"/>
                    </Button>
                    <span className="text-xs">{page} / {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={page>=totalPages} onClick={()=>{setPage(page+1);fetchOffers(page+1);}}>
                      <ChevronRight className="h-4 w-4"/>
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
